import {
  PrintServiceProductEntity,
  PrintServiceProductImageEntity,
} from "@jackfruit/common"
import { SagaIterator } from "@redux-saga/types"
import { PayloadAction } from "@reduxjs/toolkit"
import { call, select, take, takeEvery } from "redux-saga/effects"
import { getMinAndMaxImagesForPhotoBookProduct } from "~/components/editors/photo-book/PhotoBookUtils"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { LineItemEntity } from "~/interfaces/entities/LineItem"
import { actions, UpdateMissingImagesForLineItem } from "../process"
import { printServiceProductImagesSelector } from "../state/printServiceProductImages"
import { printServiceProductsSelector } from "../state/printServiceProducts"
import { RootState } from "../store"
import { getAllImageRegionsForLineItem } from "./imageRegion"
import { getLineItem } from "./lineItems"
import { insertImagesToLineItem } from "./processLineItems"

export default function* watchUpdateMissingImagesForLineItem() {
  yield takeEvery(
    actions.updateMissingImagesForLineItem.type,
    processUpdateMissingImagesForLineItem
  )
}

function* processUpdateMissingImagesForLineItem(
  action: PayloadAction<UpdateMissingImagesForLineItem>
): SagaIterator<any> {
  const { lineItemId, files, imageRegionId, onOpen, onClose } = action.payload

  const lineItem: LineItemEntity = yield call(getLineItem, lineItemId)
  const product: PrintServiceProductEntity = yield select((state: RootState) =>
    printServiceProductsSelector.selectById(state, lineItem.productId)
  )

  const isPhotoBook = product.categoryName === "photo-book"

  const allImageRegionsForLineItem: ImageRegionEntity[] = yield call(
    getAllImageRegionsForLineItem,
    lineItemId
  )

  let imageRegions = allImageRegionsForLineItem
  if (imageRegionId) {
    const imageRegionIndex = allImageRegionsForLineItem.findIndex(
      imageRegion => imageRegion.id === imageRegionId
    )

    imageRegions = allImageRegionsForLineItem.slice(imageRegionIndex)
  }

  const imageRegionsCount = imageRegions.length
  const uploadedImagesCount = imageRegions.filter(imageRegion =>
    Boolean(imageRegion.uploadId)
  ).length

  // images has already been applied to line item
  // but some pages are still empty
  const requireConfirmation =
    uploadedImagesCount > 0 &&
    uploadedImagesCount < imageRegionsCount &&
    !imageRegionId

  // photo books can grow by adding page spreads, warn the user
  // when the new images would exceed the maximum image count
  let showExceedMaxImagesWarning = false
  if (isPhotoBook) {
    const printServiceProductImages: PrintServiceProductImageEntity[] =
      yield select((state: RootState) =>
        printServiceProductImagesSelector.selectByIds(
          state,
          product.printServiceProductImages
        )
      )
    const { maxImages } = getMinAndMaxImagesForPhotoBookProduct({
      ...product,
      printServiceProductImages,
    })

    // when the selected image region already has an image, the first file
    // replaces it instead of consuming an empty image region
    const isReplacingSelectedImageRegion =
      Boolean(imageRegionId) && Boolean(imageRegions[0]?.uploadId)

    // image regions to add equals
    // number of uploading images + number of uploaded images - number of existing image regions
    const imageRegionAmountToAdd =
      files.length +
      uploadedImagesCount -
      imageRegionsCount -
      (isReplacingSelectedImageRegion ? 1 : 0)
    const imageRegionMaxAmountToAdd = maxImages - imageRegionsCount

    showExceedMaxImagesWarning =
      imageRegionAmountToAdd > imageRegionMaxAmountToAdd
  }

  let confirm = true
  if (showExceedMaxImagesWarning && onOpen) {
    onOpen?.("warning")
    const {
      payload: { choice },
    } = yield take(actions.setUpdateMissingImagesDialogChoice.type)
    onClose?.()
    confirm = choice
  }

  let shouldAskConfirmation = requireConfirmation && Boolean(onOpen)
  if (isPhotoBook) {
    shouldAskConfirmation =
      requireConfirmation && !showExceedMaxImagesWarning && Boolean(onOpen)
  }

  if (shouldAskConfirmation) {
    onOpen?.()
    const {
      payload: { choice },
    } = yield take(actions.setUpdateMissingImagesDialogChoice.type)
    onClose?.()
    confirm = choice
  }

  if (confirm) {
    yield call(insertImagesToLineItem, {
      lineItemId,
      imageRegionId,
      sources: files,
    })
  }
}
